import React, { useState } from 'react'
import { Upload, FileText, Download, AlertCircle, CheckCircle } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Progress } from '@/components/ui/progress'
import { evaluationService } from '@/services/evaluationService'
import { toast } from 'sonner'

interface BulkImportTestCasesModalProps {
  testSetId: string
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface ParsedTestCase {
  question: string
  goldenAnswer: string
  category?: string
  difficulty?: 'easy' | 'medium' | 'hard'
  tags?: string[]
}

export const BulkImportTestCasesModal: React.FC<BulkImportTestCasesModalProps> = ({
  testSetId,
  isOpen,
  onClose,
  onSuccess
}) => {
  const [importText, setImportText] = useState('')
  const [parsedTestCases, setParsedTestCases] = useState<ParsedTestCase[]>([])
  const [isProcessing, setIsProcessing] = useState(false)
  const [importProgress, setImportProgress] = useState(0)
  const [showPreview, setShowPreview] = useState(false)

  const handleClose = () => {
    setImportText('')
    setParsedTestCases([])
    setShowPreview(false)
    setImportProgress(0)
    onClose()
  }

  const parseTestCases = () => {
    if (!importText.trim()) {
      toast.error('Please enter test cases to import')
      return
    }

    try {
      // Try to parse as JSON first
      const jsonData = JSON.parse(importText)
      if (Array.isArray(jsonData)) {
        setParsedTestCases(jsonData)
        setShowPreview(true)
        return
      }
    } catch {
      // If not JSON, try to parse as CSV/TSV
      const lines = importText.trim().split('\n')
      const testCases: ParsedTestCase[] = []
      
      // Skip header line if it exists
      const startIndex = lines[0]?.toLowerCase().includes('question') ? 1 : 0
      
      for (let i = startIndex; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) continue

        // Try CSV parsing (comma-separated)
        const csvParts = line.split(',').map(part => part.trim().replace(/^"(.*)"$/, '$1'))
        if (csvParts.length >= 2) {
          testCases.push({
            question: csvParts[0],
            goldenAnswer: csvParts[1],
            category: csvParts[2] || undefined,
            difficulty: (csvParts[3] as 'easy' | 'medium' | 'hard') || undefined,
            tags: csvParts[4] ? csvParts[4].split(';').map(t => t.trim()) : undefined
          })
          continue
        }

        // Try TSV parsing (tab-separated)
        const tsvParts = line.split('\t').map(part => part.trim())
        if (tsvParts.length >= 2) {
          testCases.push({
            question: tsvParts[0],
            goldenAnswer: tsvParts[1],
            category: tsvParts[2] || undefined,
            difficulty: (tsvParts[3] as 'easy' | 'medium' | 'hard') || undefined,
            tags: tsvParts[4] ? tsvParts[4].split(';').map(t => t.trim()) : undefined
          })
          continue
        }

        // Try simple format (question on one line, answer on next)
        if (i + 1 < lines.length) {
          testCases.push({
            question: line,
            goldenAnswer: lines[i + 1].trim()
          })
          i++ // Skip next line as it's the answer
        }
      }

      if (testCases.length === 0) {
        toast.error('No valid test cases found. Please check the format.')
        return
      }

      setParsedTestCases(testCases)
      setShowPreview(true)
    }
  }

  const handleImport = async () => {
    if (parsedTestCases.length === 0) return

    try {
      setIsProcessing(true)
      setImportProgress(0)

      // Import test cases in batches
      const batchSize = 10
      const batches = []
      for (let i = 0; i < parsedTestCases.length; i += batchSize) {
        batches.push(parsedTestCases.slice(i, i + batchSize))
      }

      for (let i = 0; i < batches.length; i++) {
        const batch = batches[i]
        await evaluationService.bulkAddTestCases(testSetId, { testCases: batch })
        
        const progress = ((i + 1) / batches.length) * 100
        setImportProgress(progress)
      }

      toast.success(`Successfully imported ${parsedTestCases.length} test cases`)
      onSuccess?.()
      handleClose()
    } catch (error) {
      console.error('Error importing test cases:', error)
      toast.error('Failed to import test cases')
    } finally {
      setIsProcessing(false)
    }
  }

  const downloadTemplate = () => {
    const template = `Question,Expected Answer,Category,Difficulty,Tags
"What is our vacation policy?","Employees get 15 days PTO per year","HR Policy","easy","policy;vacation"
"How do I file an expense report?","Use the expense portal in our intranet","Procedures","medium","expenses;process"
"What are the office hours?","Monday-Friday 9 AM to 5 PM","General","easy","hours;schedule"`

    const blob = new Blob([template], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = 'test-cases-template.csv'
    link.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bulk Import Test Cases</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {!showPreview ? (
            <>
              {/* Instructions */}
              <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 p-4 rounded-lg">
                <h3 className="font-medium mb-2">Import Formats Supported:</h3>
                <ul className="text-sm space-y-1">
                  <li>• <strong>JSON:</strong> Array of objects with question, goldenAnswer, category, difficulty, tags</li>
                  <li>• <strong>CSV:</strong> Comma-separated values with header row</li>
                  <li>• <strong>TSV:</strong> Tab-separated values</li>
                  <li>• <strong>Simple:</strong> Question on one line, answer on next line</li>
                </ul>
              </div>

              {/* Template Download */}
              <div className="flex justify-between items-center">
                <Label htmlFor="importText">Import Data</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={downloadTemplate}
                  className="gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Template
                </Button>
              </div>

              {/* Import Text Area */}
              <Textarea
                id="importText"
                placeholder="Paste your test cases here..."
                value={importText}
                onChange={(e) => setImportText(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
              />

              {/* Parse Button */}
              <Button onClick={parseTestCases} className="w-full gap-2">
                <FileText className="h-4 w-4" />
                Parse Test Cases
              </Button>
            </>
          ) : (
            <>
              {/* Preview */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium">
                    Preview ({parsedTestCases.length} test cases)
                  </h3>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setShowPreview(false)}
                  >
                    Edit Import Data
                  </Button>
                </div>

                {/* Progress Bar */}
                {isProcessing && (
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span>Importing test cases...</span>
                      <span>{importProgress.toFixed(0)}%</span>
                    </div>
                    <Progress value={importProgress} />
                  </div>
                )}

                {/* Preview List */}
                <div className="max-h-[400px] overflow-y-auto space-y-2">
                  {parsedTestCases.slice(0, 10).map((testCase, index) => (
                    <div key={index} className="border rounded-lg p-3 space-y-2">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline">#{index + 1}</Badge>
                        {testCase.category && (
                          <Badge variant="secondary">{testCase.category}</Badge>
                        )}
                        {testCase.difficulty && (
                          <Badge 
                            variant={testCase.difficulty === 'hard' ? 'destructive' : 
                                    testCase.difficulty === 'medium' ? 'secondary' : 'outline'}
                          >
                            {testCase.difficulty}
                          </Badge>
                        )}
                      </div>
                      <div className="text-sm">
                        <div className="font-medium">Q: {testCase.question}</div>
                        <div className="text-muted-foreground">
                          A: {testCase.goldenAnswer.substring(0, 100)}
                          {testCase.goldenAnswer.length > 100 ? '...' : ''}
                        </div>
                      </div>
                      {testCase.tags && testCase.tags.length > 0 && (
                        <div className="flex gap-1">
                          {testCase.tags.map((tag, tagIndex) => (
                            <Badge key={tagIndex} variant="outline" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {parsedTestCases.length > 10 && (
                    <div className="text-center py-2 text-sm text-muted-foreground">
                      ... and {parsedTestCases.length - 10} more test cases
                    </div>
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {showPreview && (
            <Button
              onClick={handleImport}
              disabled={isProcessing || parsedTestCases.length === 0}
              className="gap-2"
            >
              {isProcessing ? (
                <>
                  <Upload className="h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Import {parsedTestCases.length} Test Cases
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}